import React, { useEffect, useRef, useState } from 'react';
import { MoreVertical } from 'lucide-react';

export interface ReceiptActionMenuItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

interface ReceiptActionMenuProps {
  items: ReceiptActionMenuItem[];
  /** Tilgjengelig navn på trigger-knappen (aria-label + title). */
  label: string;
}

/**
 * ReceiptActionMenu — overflow-meny for sekundære handlinger i varemottak-detalj.
 *
 * Under `lg` er det ikke plass til 7-8 knapper på én rad. Uten meny skjules etikettene
 * (`hidden sm:inline`) og operatøren sitter igjen med nakne ikoner der eneste forklaring
 * er et `title`-attributt — som aldri vises på touch. Menyen gir sekundærhandlingene
 * tilbake sine etiketter, og lar primærhandlingene beholde plassen sin.
 *
 * Rendres ikke når `items` er tom.
 */
export const ReceiptActionMenu: React.FC<ReceiptActionMenuProps> = ({ items, label }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  if (items.length === 0) return null;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        title={label}
        className={`flex items-center justify-center min-h-[44px] min-w-[44px] px-3 py-2 rounded-lg transition-colors ${
          open ? 'bg-gray-300 text-gray-900' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
        }`}
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 w-56 max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          {items.map(item => (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                setOpen(false);
                item.onClick();
              }}
              className="flex w-full items-center gap-3 min-h-[44px] px-3 py-2 text-left text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
            >
              <span className="flex-shrink-0 text-gray-500">{item.icon}</span>
              <span className="truncate">{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReceiptActionMenu;
